import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, ShieldAlert, Trash2, Plus, Check } from "lucide-react";
import { api, apiError } from "@/lib/api";

const CHECKER_BG = {
  backgroundImage:
    "conic-gradient(#000 25%, #fff 0 50%, #000 0 75%, #fff 0)",
  backgroundSize: "12px 12px",
};

const clamp01 = (v) => Math.min(1, Math.max(0, v));
let BOX_SEQ = 0;

// Interactive editor for ONE image: move / resize / add / delete cover boxes.
// Boxes are stored as fractions of the image so they stay correct at any display size.
function BoxEditor({ url, boxes, setBoxes }) {
  const ref = useRef(null);
  const drag = useRef(null);

  const toFrac = useCallback((e) => {
    const r = ref.current.getBoundingClientRect();
    return { fx: clamp01((e.clientX - r.left) / r.width), fy: clamp01((e.clientY - r.top) / r.height) };
  }, []);

  useEffect(() => {
    const move = (e) => {
      const d = drag.current;
      if (!d) return;
      const { fx, fy } = toFrac(e);
      setBoxes((prev) => prev.map((b) => {
        if (b.id !== d.id) return b;
        if (d.mode === "create" || d.mode === "resize") {
          const nx = Math.min(d.ox, fx), ny = Math.min(d.oy, fy);
          return { ...b, fx: nx, fy: ny, fw: Math.abs(fx - d.ox), fh: Math.abs(fy - d.oy) };
        }
        if (d.mode === "move") {
          return { ...b, fx: clamp01(d.bx + (fx - d.ox)), fy: clamp01(d.by + (fy - d.oy)) };
        }
        return b;
      }));
    };
    const up = () => {
      const d = drag.current;
      if (d && (d.mode === "create" || d.mode === "resize")) {
        setBoxes((prev) => prev.filter((b) => b.fw > 0.015 && b.fh > 0.015));
      }
      drag.current = null;
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  }, [setBoxes, toFrac]);

  const startCreate = (e) => {
    if (e.target.dataset.boxpart) return; // clicked a box/handle, not the canvas
    e.preventDefault();
    const { fx, fy } = toFrac(e);
    const id = `m${++BOX_SEQ}`;
    drag.current = { id, mode: "create", ox: fx, oy: fy };
    setBoxes((prev) => [...prev, { id, fx, fy, fw: 0, fh: 0, src: "manual" }]);
  };

  return (
    <div
      ref={ref}
      data-testid="censor-canvas"
      onPointerDown={startCreate}
      className="relative w-full select-none touch-none rounded-lg overflow-hidden border border-white/10 bg-black"
      style={{ cursor: "crosshair" }}
    >
      <img src={url} alt="" draggable={false} className="block w-full h-auto pointer-events-none" />
      {boxes.map((b) => (
        <div
          key={b.id}
          data-boxpart="1"
          data-testid="censor-box"
          onPointerDown={(e) => {
            e.stopPropagation();
            const { fx, fy } = toFrac(e);
            drag.current = { id: b.id, mode: "move", ox: fx, oy: fy, bx: b.fx, by: b.fy };
          }}
          className="absolute border-2 border-cyan-300/90"
          style={{ left: `${b.fx * 100}%`, top: `${b.fy * 100}%`, width: `${b.fw * 100}%`, height: `${b.fh * 100}%`, ...CHECKER_BG, cursor: "move" }}
          title={b.cls ? `${b.cls} ${(b.score * 100) | 0}%` : "manual cover"}
        >
          <button
            type="button"
            data-boxpart="1"
            data-testid="censor-box-delete"
            onPointerDown={(e) => { e.stopPropagation(); }}
            onClick={(e) => { e.stopPropagation(); setBoxes((prev) => prev.filter((x) => x.id !== b.id)); }}
            className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-red-500 text-white flex items-center justify-center shadow"
          >
            <Trash2 className="h-3 w-3" />
          </button>
          <span
            data-boxpart="1"
            onPointerDown={(e) => {
              e.stopPropagation();
              drag.current = { id: b.id, mode: "resize", ox: b.fx, oy: b.fy };
            }}
            className="absolute -bottom-1.5 -right-1.5 h-4 w-4 rounded-sm bg-cyan-300 border border-black cursor-nwse-resize"
          />
        </div>
      ))}
    </div>
  );
}

export default function CensorReviewDialog({ open, onOpenChange, cards, onConfirm }) {
  const urls = useMemo(
    () => [...new Set((cards || []).map((c) => c.front_image).filter(Boolean))],
    [cards]
  );
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [imgs, setImgs] = useState([]); // [{url, width, height, boxes:[]}]
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!open) { setImgs([]); setIdx(0); return; }
    if (!urls.length) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.post("/admin/print/censor/detect", { images: urls });
        if (cancelled) return;
        const built = urls.map((url) => {
          const r = data.results[url] || { width: 0, height: 0, boxes: [] };
          const W = r.width || 1, H = r.height || 1;
          return {
            url, width: r.width, height: r.height, error: r.error,
            boxes: (r.boxes || []).map((b) => ({
              id: b.id || `d${++BOX_SEQ}`, fx: b.x / W, fy: b.y / H, fw: b.w / W, fh: b.h / H,
              cls: b.cls, score: b.score, src: "auto",
            })),
          };
        });
        setImgs(built);
        setIdx(0);
      } catch (e) {
        toast.error(apiError(e, "Detection failed"));
        onOpenChange(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, urls, onOpenChange]);

  const setBoxesFor = useCallback((i, updater) => {
    setImgs((prev) => prev.map((im, j) => (j === i ? { ...im, boxes: typeof updater === "function" ? updater(im.boxes) : updater } : im)));
  }, []);

  const totalBoxes = imgs.reduce((n, im) => n + im.boxes.length, 0);
  const current = imgs[idx];

  const apply = async () => {
    setApplying(true);
    try {
      const items = imgs
        .filter((im) => im.boxes.length && im.width && im.height)
        .map((im) => ({
          url: im.url,
          boxes: im.boxes.map((b) => ({
            x: b.fx * im.width, y: b.fy * im.height, w: b.fw * im.width, h: b.fh * im.height,
          })),
        }));
      let map = {};
      if (items.length) {
        const { data } = await api.post("/admin/print/censor/apply", { items });
        map = data.map || {};
      }
      onConfirm(map);
    } catch (e) {
      toast.error(apiError(e, "Could not apply censoring"));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!applying) onOpenChange(o); }}>
      <DialogContent data-testid="censor-review-dialog" className="bg-[hsl(222_22%_8%)] border-white/10 max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-[0.06em] flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-amber-300" /> REVIEW CENSORED AREAS
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-white/65 -mt-1" data-testid="censor-help">
          Checkered boxes cover nipples, genitalia and exposed anus in the <strong className="text-white/80">printed PDF only</strong> — redeemed digital cards stay uncensored.
          Detection is automatic; <strong className="text-white/80">drag on the image to add a cover</strong>, drag a box to move it, use the corner to resize, or the trash icon to remove a false positive.
        </p>

        {loading ? (
          <div className="py-16 flex items-center justify-center gap-2 text-white/60" data-testid="censor-detecting">
            <Loader2 className="h-5 w-5 animate-spin" /> Scanning {urls.length} image{urls.length === 1 ? "" : "s"}…
          </div>
        ) : current ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-xs text-white/50">
              <span data-testid="censor-image-counter">Image {idx + 1} of {imgs.length}</span>
              <span data-testid="censor-box-count">{current.boxes.length} cover{current.boxes.length === 1 ? "" : "s"} here · {totalBoxes} total</span>
            </div>

            <div className="max-h-[52vh] overflow-auto flex justify-center">
              <div className="max-w-sm w-full">
                <BoxEditor url={current.url} boxes={current.boxes} setBoxes={(u) => setBoxesFor(idx, u)} />
                {current.error && <div className="text-[11px] text-amber-300 mt-1">Could not read this image for detection — you can still add covers manually.</div>}
              </div>
            </div>

            {imgs.length > 1 && (
              <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="censor-thumbs">
                {imgs.map((im, j) => (
                  <button key={im.url} type="button" data-testid="censor-thumb" onClick={() => setIdx(j)}
                    className={`relative shrink-0 h-14 w-11 rounded overflow-hidden border ${j === idx ? "border-cyan-300" : "border-white/15"}`}>
                    <img src={im.url} alt="" className="w-full h-full object-cover" />
                    {im.boxes.length > 0 && (
                      <span className="absolute top-0 right-0 bg-amber-500 text-black text-[9px] font-bold px-1 rounded-bl">{im.boxes.length}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="py-12 text-center text-white/50" data-testid="censor-none">No artwork to review.</div>
        )}

        <div className="flex gap-2 mt-1">
          <Button type="button" variant="outline" data-testid="censor-cancel-button" disabled={applying} onClick={() => onOpenChange(false)}
            className="flex-1 border-white/15 bg-transparent text-white hover:bg-white/5">Cancel</Button>
          <Button type="button" data-testid="censor-apply-button" disabled={applying || loading} onClick={apply}
            className="flex-1 bg-amber-500 text-black hover:bg-amber-400">
            {applying ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Applying…</> : <><Check className="h-4 w-4 mr-2" /> Apply & make PDF ({totalBoxes})</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
